import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Text, Box, Group, TextInput, Drawer, Button, Input, SegmentedControl, Paper, ActionIcon } from '@mantine/core';
import { IconChevronRight, IconBarcode, IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { safeValue, isoDateToSqlDate, sqlDateToIsoDate, regexNumberOnly, numberWithComma, regexNoSpace, regex0to29 } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import ModalResponse from "../../components/general/ModalResponse";
import PickerDate from "../../components/general/PickerDate";

import StockCategorySubSelectorDrawer from "../../components/utilities/StockCategorySubSelectorDrawer";
import SupplierSelectorDrawer from "../../components/utilities/SupplierSelectorDrawer";
import ScannerBarQRCodeModul from "../serialNumberChecker/ScannerBarQRCodeModul";

const StockItemEdit = ({
    stateShow,
    fnShow,
    fnSuccessEdit,
    stateData,
}) => {

    const mountedRef = React.useRef(true);
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorSubmitData: 'stockItemEdit-1',
        successSubmitData: 'stockItemEdit-2',
    })

    const constStockCategorySub = {
        id: 'editStockCategorySub',
        label: 'Sub Kategori',
        withAsterisk: true,
        value: {
            show: false,
            id: '',
            name: '',
            isActive: '',
            categoryId: '',
            categoryName: ''
        },
        description: '',
        error: ''
    }

    const constStockSupplier = {
        id: 'editStockSupplier',
        label: 'Supplier',
        withAsterisk: true,
        value: {
            show: false,
            id: '',
            name: '',
            address: '',
            contact: '',
            isActive: '',
        },
        description: '',
        error: '',
    }

    const constStockTanggalBeli = {
        id: 'editStockTanggalBeli',
        label: 'Tanggal Pembelian',
        withAsterisk: true,
        placeholder: 'Tanggal Pembelian',
        value: new Date(),
        description: '',
        error: ''
    }

    const constStockHargaBeli = {
        id: 'editStockHargaBeli',
        label: 'Harga Pembelian',
        withAsterisk: true,
        placeholder: 'Harga Pembelian',
        value: '',
        description: '',
        error: ''
    }

    const constStockHargaJual = {
        id: 'editStockHargaJual',
        label: 'Harga Penjualan',
        withAsterisk: true,
        placeholder: 'Harga Penjualan',
        value: '',
        description: '',
        error: ''
    }

    const constStockMasaGaransi = {
        id: 'editStockMasaGaransi',
        label: 'Masa Garansi',
        withAsterisk: true,
        placeholder: 'Masa Garansi',
        value: {
            flag: utils.masaGaransi.berjangka.value,
            bulan: '',
            hari: '',
        },
        description: '',
        error: ''
    }

    const constStockItem = {
        id: 'editStockItem',
        label: 'STOK ITEM',
        withAsterisk: true,
        value: {
            serialNumber: '',
            jumlah: '',
        },
        description: 'Jumlah otomatis terisi 1 jika item memiliki serial number',
        error: ''
    }

    const constStockIsActive = {
        id: 'editStockIsActive',
        label: 'Status',
        withAsterisk: true,
        placeholder: 'Status',
        value: '1',
        description: '',
        error: ''
    }

    const [data, setData] = React.useState({
        stockCategorySub: constStockCategorySub,
        stockSupplier: constStockSupplier,
        stockTanggalBeli: constStockTanggalBeli,
        stockHargaBeli: constStockHargaBeli,
        stockHargaJual: constStockHargaJual,
        stockMasaGaransi: constStockMasaGaransi,
        stockItem: constStockItem,
        stockIsActive: constStockIsActive,

        submitLoading: false,
        resetData: new Date(),

        scannerModal: false,

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    const getMasaGaransiNumber = () => {
        if (data.stockMasaGaransi.value.flag === utils.masaGaransi.berjangka.value) {
            return {
                bulan: Number(data.stockMasaGaransi.value.bulan),
                hari: Number(data.stockMasaGaransi.value.hari)
            }
        } else {
            return {
                bulan: 0,
                hari: 0
            }
        }
    }

    React.useEffect(() => {
        setData(d => ({
            ...d,
            stockCategorySub: {
                ...d.stockCategorySub,
                value: {
                    ...d.stockCategorySub.value,
                    id: stateData.stock_category_sub_id,
                    name: stateData.stock_category_sub_name,
                    isActive: stateData.stock_category_sub_is_active,
                    categoryId: stateData.stock_category_id,
                    categoryName: stateData.stock_category_name,
                },
                error: '',
            },
            stockSupplier: {
                ...d.stockSupplier,
                value: {
                    ...d.stockSupplier.value,
                    id: stateData.supplier_id,
                    name: stateData.supplier_name,
                    address: stateData.supplier_address,
                    contact: stateData.supplier_contact,
                    isActive: stateData.supplier_is_active,
                },
                error: '',
            },
            stockTanggalBeli: {
                ...d.stockTanggalBeli,
                value: sqlDateToIsoDate(stateData.stock_tanggal_beli, null),
                error: '',
            },
            stockHargaBeli: {
                ...d.stockHargaBeli,
                value: safeValue(stateData.stock_harga_beli, ''),
                error: '',
            },
            stockHargaJual: {
                ...d.stockHargaJual,
                value: safeValue(stateData.stock_harga_jual, ''),
                error: '',
            },
            stockMasaGaransi: {
                ...d.stockMasaGaransi,
                value: {
                    ...d.stockMasaGaransi.value,
                    bulan: safeValue(stateData.stock_masa_garansi_bulan, ''),
                    hari: safeValue(stateData.stock_masa_garansi_hari, ''),
                    flag: stateData.stock_masa_garansi_type,
                },
                error: '',
            },
            stockItem: {
                ...d.stockItem,
                value: {
                    ...d.stockItem.value,
                    serialNumber: safeValue(stateData.stock_serial_number, ''),
                    jumlah: safeValue(stateData.stock_jumlah, ''),
                },
                error: '',
            },
            stockIsActive: {
                ...d.stockIsActive,
                value: safeValue(stateData.stock_is_active, '1'),
                error: '',
            },
        }))
    }, [stateData, data.resetData])

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const resetData = () => {
        setData(d => ({ ...d, resetData: new Date() }))
    }

    const editData = () => {
        setData(d => ({
            ...d,
            stockCategorySub: { ...d.stockCategorySub, error: '' },
            stockSupplier: { ...d.stockSupplier, error: '' },
            stockTanggalBeli: { ...d.stockTanggalBeli, error: '' },
            stockHargaBeli: { ...d.stockHargaBeli, error: '' },
            stockHargaJual: { ...d.stockHargaJual, error: '' },
            stockMasaGaransi: { ...d.stockMasaGaransi, error: '' },
            stockItem: { ...d.stockItem, error: '' },
        }));

        if (validator.isEmpty(data.stockCategorySub.value.id, { ignore_whitespace: true })) {
            setData(d => ({ ...d, stockCategorySub: { ...d.stockCategorySub, error: 'Sub kategori wajib diisi!', } }));
        } else if (validator.isEmpty(data.stockSupplier.value.id, { ignore_whitespace: true })) {
            setData(d => ({ ...d, stockSupplier: { ...d.stockSupplier, error: 'Supplier wajib diisi!', } }));
        } else if (!validator.isDate(data.stockTanggalBeli.value)) {
            setData(d => ({ ...d, stockTanggalBeli: { ...d.stockTanggalBeli, error: 'Tanggal pembelian wajib diisi!', } }));
        } else if (validator.isEmpty(data.stockHargaBeli.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, stockHargaBeli: { ...d.stockHargaBeli, error: 'Harga pembelian wajib diisi!', } }));
        } else if (validator.isEmpty(data.stockHargaJual.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, stockHargaJual: { ...d.stockHargaJual, error: 'Harga penjualan wajib diisi!' } }))
        }

        else if ((data.stockMasaGaransi.value.flag === utils.masaGaransi.berjangka.value) && ((Number(data.stockMasaGaransi.value.bulan) === 0) && (Number(data.stockMasaGaransi.value.hari) === 0))) {
            setData(d => ({ ...d, stockMasaGaransi: { ...d.stockMasaGaransi, error: 'Masa garansi wajib diisi!' } }))
        }

        else if ((data.stockMasaGaransi.value.flag === utils.masaGaransi.berjangka.value) && ((Number(data.stockMasaGaransi.value.bulan) === 0) || Number(data.stockMasaGaransi.value.bulan) > 0) && !regex0to29({ stateData: data.stockMasaGaransi.value.hari, stateCanEmpty: true })) {
            setData(d => ({ ...d, stockMasaGaransi: { ...d.stockMasaGaransi, error: 'Masa garansi (hari) yaitu antara 0-29 hari !' } }))
        }

        else if (data.stockItem.value.serialNumber === '' && data.stockItem.value.jumlah === '') {
            setData(d => ({
                ...d,
                modalResponse: {
                    ...d.modalResponse,
                    type: utils.modalResponse.warning,
                    hideOnClickOutSide: false,
                    content: 'Lengkapi stok item terlebih dahulu',
                    show: true
                }
            }))
        } else if (data.stockItem.value.serialNumber !== '' && data.stockItem.value.jumlah !== '1') {
            setData(d => ({
                ...d,
                modalResponse: {
                    ...d.modalResponse,
                    type: utils.modalResponse.warning,
                    hideOnClickOutSide: false,
                    content: 'Pengisian serial number dan jumlah harus sesuai ketentuan',
                    show: true
                }
            }))
        } else if (Number(data.stockItem.value.jumlah) <= 0) {
            setData(d => ({
                ...d,
                modalResponse: {
                    ...d.modalResponse,
                    type: utils.modalResponse.warning,
                    hideOnClickOutSide: false,
                    content: 'Input jumlah minimal 1',
                    show: true
                }
            }))
        } else {

            setData(d => ({ ...d, submitLoading: true }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('stock_id', stateData.stock_id);
            formDT.append('stock_category_sub_id', data.stockCategorySub.value.id);
            formDT.append('stock_category_sub_name', data.stockCategorySub.value.name);
            formDT.append('stock_supplier', data.stockSupplier.value.id);
            formDT.append('stock_tanggal_beli', isoDateToSqlDate(data.stockTanggalBeli.value, ''));
            formDT.append('stock_harga_beli', data.stockHargaBeli.value);
            formDT.append('stock_harga_jual', data.stockHargaJual.value);

            formDT.append('stock_masa_garansi_type', data.stockMasaGaransi.value.flag);
            formDT.append('stock_masa_garansi_bulan', getMasaGaransiNumber().bulan);
            formDT.append('stock_masa_garansi_hari', getMasaGaransiNumber().hari);

            // formDT.append('stock_status', utils.stockItemStatus.new.value);
            formDT.append('stock_item_serial_number', data.stockItem.value.serialNumber);
            formDT.append('stock_item_jumlah', data.stockItem.value.jumlah);
            formDT.append('stock_is_active', data.stockIsActive.value);

            axios({
                method: 'post',
                url: BE_API_ONE + '/edit_stock_item',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                if (response.data.flag_status === 'isset') {

                    fnSuccessEdit();

                    notifications.show({
                        id: notificationId.current.successSubmitData,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Edit Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'duplicate-serial-number') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Serial number ${response.data.suspect} sudah pernah didaftarkan pada sub kategori ${response.data.suspect_2}`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: false,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Drawer
                opened={stateShow}
                onClose={fnShow}
                withCloseButton={false}
                position="left"
                closeOnClickOutside={true}

                title={
                    <Group noWrap spacing='xs'>
                        <ActionIcon
                            size='lg'
                            variant="light"
                            radius='xl'
                            color="blue"
                            onClick={fnShow}
                        >
                            <IconArrowLeft size='1.25rem' stroke={1.5} />
                        </ActionIcon>

                        <Text>Edit Stok Item</Text>
                    </Group>
                }
                size='md'

                styles={() => ({
                    title: {
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }
                })}
            >
                <Box py='md' sx={{ minHeight: '500px' }}>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockCategorySub.label}
                            stateWithAsterisk={data.stockCategorySub.withAsterisk}
                            stateError={data.stockCategorySub.error}
                            stateDescription={data.stockCategorySub.description}
                        >
                            <Paper
                                sx={(tema) => ({
                                    backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6],
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: tema.colorScheme === 'light' ? tema.colors.gray[4] : tema.colors.dark[4],
                                    cursor: 'pointer',
                                })}

                                onClick={() => {
                                    setData(d => ({
                                        ...d,
                                        stockCategorySub: {
                                            ...d.stockCategorySub,
                                            value: {
                                                ...d.stockCategorySub.value,
                                                show: true
                                            }
                                        }
                                    }));
                                }}
                            >
                                {data.stockCategorySub.value.id === '' &&
                                    <Group
                                        position="apart"
                                        noWrap
                                        p="xs"
                                    >
                                        <Text size='sm'>Pilih Sub Kategori</Text>
                                        <IconChevronRight size="1rem" />
                                    </Group>
                                }

                                {data.stockCategorySub.value.id &&
                                    <Group
                                        position="apart"
                                        noWrap
                                        p="xs"
                                    >
                                        <Group noWrap>
                                            <Text size='sm'>
                                                <Text>
                                                    {data.stockCategorySub.value.name}
                                                </Text>

                                                <Text size='xs'>
                                                    Kategori {data.stockCategorySub.value.categoryName}
                                                </Text>

                                                <Text size='xs'>
                                                    Status Sub Kategori {data.stockCategorySub.value.isActive === '1' ? 'Aktif' : 'Non Aktif'}
                                                </Text>
                                            </Text>
                                        </Group>

                                        <IconChevronRight size="1.25rem" />
                                    </Group>
                                }
                            </Paper>
                        </InputWrapperCustom>
                    </Box>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockSupplier.label}
                            stateWithAsterisk={data.stockSupplier.withAsterisk}
                            stateError={data.stockSupplier.error}
                            stateDescription={data.stockSupplier.description}
                        >
                            <Paper
                                sx={(tema) => ({
                                    backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6],
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: tema.colorScheme === 'light' ? tema.colors.gray[4] : tema.colors.dark[4],
                                    cursor: 'pointer',
                                })}

                                onClick={() => {
                                    setData(d => ({
                                        ...d,
                                        stockSupplier: {
                                            ...d.stockSupplier,
                                            value: {
                                                ...d.stockSupplier.value,
                                                show: true
                                            }
                                        }
                                    }));
                                }}
                            >
                                {data.stockSupplier.value.id === '' &&
                                    <Group
                                        position="apart"
                                        noWrap
                                        p="xs"
                                    >
                                        <Text size='sm'>Pilih Supplier</Text>
                                        <IconChevronRight size="1rem" />
                                    </Group>
                                }

                                {data.stockSupplier.value.id &&
                                    <Group
                                        position="apart"
                                        noWrap
                                        p="xs"
                                    >
                                        <Group noWrap>
                                            <Text size='sm'>
                                                <Text>
                                                    {data.stockSupplier.value.name}
                                                </Text>

                                                <Text size='xs'>
                                                    Alamat {data.stockSupplier.value.address ? data.stockSupplier.value.address : '-'}
                                                </Text>

                                                <Text size='xs'>
                                                    Kontak {data.stockSupplier.value.contact ? data.stockSupplier.value.contact : '-'}
                                                </Text>

                                                <Text size='xs'>
                                                    Status Supplier {data.stockSupplier.value.isActive === '1' ? 'Aktif' : 'Non Aktif'}
                                                </Text>
                                            </Text>
                                        </Group>

                                        <IconChevronRight size="1.25rem" />
                                    </Group>
                                }
                            </Paper>
                        </InputWrapperCustom>
                    </Box>

                    <Box mb='2rem'>
                        <PickerDate
                            stateMinDate={null}
                            stateDate={data.stockTanggalBeli.value}

                            fnSetDate={(val) => setData(d => ({
                                ...d,
                                stockTanggalBeli: {
                                    ...d.stockTanggalBeli,
                                    value: val
                                }
                            }))}

                            stateLabel={data.stockTanggalBeli.label}
                            statePlaceHolder={data.stockTanggalBeli.placeholder}
                            stateWithAsterisk={data.stockTanggalBeli.withAsterisk}
                            stateDescription={data.stockTanggalBeli.description}
                            stateError={data.stockTanggalBeli.error}
                        />
                    </Box>

                    <Box mb='2rem'>
                        <InputComponent
                            stateId={data.stockHargaBeli.id}
                            stateWithAsterisk={data.stockHargaBeli.withAsterisk}
                            stateLabel={data.stockHargaBeli.label}
                            stateDescription={data.stockHargaBeli.description}
                            statePlaceHolder={data.stockHargaBeli.placeholder}
                            stateValue={numberWithComma(data.stockHargaBeli.value)}
                            stateError={data.stockHargaBeli.error}
                            stateIcon={<Text size='sm'>Rp</Text>}

                            fnOnChange={(e) => setData(d => ({
                                ...d,
                                stockHargaBeli: {
                                    ...d.stockHargaBeli,
                                    value: regexNumberOnly(e.target.value)
                                }
                            }))}
                        />
                    </Box>

                    <Box mb='2rem'>
                        <InputComponent
                            stateId={data.stockHargaJual.id}
                            stateWithAsterisk={data.stockHargaJual.withAsterisk}
                            stateLabel={data.stockHargaJual.label}
                            stateDescription={data.stockHargaJual.description}
                            statePlaceHolder={data.stockHargaJual.placeholder}
                            stateValue={numberWithComma(data.stockHargaJual.value)}
                            stateError={data.stockHargaJual.error}
                            stateIcon={<Text size='sm'>Rp</Text>}

                            fnOnChange={(e) => setData(d => ({
                                ...d,
                                stockHargaJual: {
                                    ...d.stockHargaJual,
                                    value: regexNumberOnly(e.target.value)
                                }
                            }))}
                        />
                    </Box>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockMasaGaransi.label}
                            stateWithAsterisk={data.stockMasaGaransi.withAsterisk}
                            stateError={data.stockMasaGaransi.error}
                            stateDescription={data.stockMasaGaransi.description}
                        >
                            <SegmentedControl
                                fullWidth
                                value={data.stockMasaGaransi.value.flag}
                                onChange={(val) => setData(d => ({
                                    ...d,
                                    stockMasaGaransi: {
                                        ...d.stockMasaGaransi,
                                        value: {
                                            ...d.stockMasaGaransi.value,
                                            flag: val
                                        }
                                    }
                                }))}

                                data={Object.values(utils.masaGaransi).map(key => ({ value: key.value, label: key.caption }))}
                            />
                        </InputWrapperCustom>

                        {data.stockMasaGaransi.value.flag === utils.masaGaransi.berjangka.value &&
                            <Group noWrap spacing='xs'>
                                <InputComponent
                                    stateId={data.stockMasaGaransi.id + '-input-bulan'}
                                    stateWithAsterisk={false}
                                    stateLabel=''
                                    stateDescription=''
                                    statePlaceHolder='0'
                                    stateValue={data.stockMasaGaransi.value.bulan}
                                    stateError=''
                                    stateStylesInput={{
                                        paddingRight: '3.25rem'
                                    }}
                                    stateRightSection={<Text
                                        size='sm'
                                        sx={{ paddingRight: '1.25rem', opacity: .5 }}
                                    >
                                        Bulan
                                    </Text>}

                                    stateMT="xs"

                                    fnOnChange={(e) => setData(d => ({
                                        ...d,
                                        stockMasaGaransi: {
                                            ...d.stockMasaGaransi,
                                            value: {
                                                ...d.stockMasaGaransi.value,
                                                bulan: regexNumberOnly(e.target.value)
                                            }
                                        }
                                    }))}
                                />

                                <InputComponent
                                    stateId={data.stockMasaGaransi.id + '-input-hari'}
                                    stateWithAsterisk={false}
                                    stateLabel=''
                                    stateDescription=''
                                    statePlaceHolder='0'
                                    stateValue={data.stockMasaGaransi.value.hari}
                                    stateError=''
                                    stateStylesInput={{
                                        paddingRight: '3.25rem'
                                    }}
                                    stateRightSection={<Text
                                        size='sm'
                                        sx={{ paddingRight: '1.25rem', opacity: .5 }}
                                    >
                                        Hari
                                    </Text>}

                                    stateMT="xs"

                                    fnOnChange={(e) => {
                                        setData(d => ({
                                            ...d,
                                            stockMasaGaransi: {
                                                ...d.stockMasaGaransi,
                                                value: {
                                                    ...d.stockMasaGaransi.value,
                                                    hari: regexNumberOnly(e.target.value)
                                                }
                                            }
                                        }));
                                    }}
                                />
                            </Group>
                        }
                    </Box>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockItem.label}
                            stateWithAsterisk={data.stockItem.withAsterisk}
                            stateError={data.stockItem.error}
                            stateErrorMT={0}
                            stateErrorSX={{ marginBottom: '0.75rem' }}
                            stateDescription={data.stockItem.description}
                        >
                            <Box
                                mb='1rem'

                                p='xs'

                                sx={(tema) => ({
                                    backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[0] : tema.colors.dark[6],
                                    borderRadius: tema.radius.sm,
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: tema.colorScheme === 'light' ? tema.colors.gray[4] : tema.colors.dark[4],
                                })}
                            >
                                <Input.Wrapper
                                    id={data.stockItem.id + '-sn'}
                                    size='sm'
                                    withAsterisk={false}
                                    labelProps={{ style: { width: '100%' } }}
                                    label={
                                        <Text color="dimmed">Serial Number</Text>
                                    }
                                    description=''
                                    sx={{ marginBottom: '.75rem' }}
                                >
                                    <TextInput
                                        id={data.stockItem.id + '-sn'}
                                        variant='default'
                                        placeholder='Serial Number'
                                        value={data.stockItem.value.serialNumber}
                                        onChange={(e) => {
                                            setData(d => ({
                                                ...d,
                                                stockItem: {
                                                    ...d.stockItem,
                                                    value: {
                                                        ...d.stockItem.value,
                                                        serialNumber: regexNoSpace(e.target.value),
                                                        jumlah: e.target.value ? '1' : d.stockItem.value.jumlah
                                                    }
                                                }
                                            }));
                                        }}

                                        rightSection={
                                            <ActionIcon
                                                onClick={() => setData(d => ({ ...d, scannerModal: true }))}

                                                color='blue'
                                                variant='filled'
                                            >
                                                <IconBarcode size="1rem" style={{ display: 'block', opacity: 1 }} />
                                            </ActionIcon>
                                        }

                                        styles={(tema) => ({
                                            input: {
                                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[7],
                                            }
                                        })}
                                    />
                                </Input.Wrapper>

                                <Input.Wrapper
                                    id={data.stockItem.id + '-jml'}
                                    size='sm'
                                    withAsterisk={false}
                                    label={<Text color="dimmed">Jumlah</Text>}
                                    description=''
                                >
                                    <TextInput
                                        id={data.stockItem.id + '-jml'}
                                        variant='default'
                                        placeholder='Jumlah'
                                        value={data.stockItem.value.jumlah}
                                        onChange={(e) => setData(d => ({
                                            ...d,
                                            stockItem: {
                                                ...d.stockItem,
                                                value: {
                                                    ...d.stockItem.value,
                                                    jumlah: d.stockItem.value.serialNumber ? '1' : regexNumberOnly(e.target.value)
                                                }
                                            }
                                        }))}

                                        styles={(tema) => ({
                                            input: {
                                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[7],
                                            }
                                        })}
                                    />
                                </Input.Wrapper>
                            </Box>
                        </InputWrapperCustom>
                    </Box>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockIsActive.label}
                            stateWithAsterisk={data.stockIsActive.withAsterisk}
                            stateError={data.stockIsActive.error}
                            stateDescription={data.stockIsActive.description}
                        >
                            <SegmentedControl
                                fullWidth
                                value={data.stockIsActive.value}
                                onChange={(val) => setData(d => ({
                                    ...d,
                                    stockIsActive: {
                                        ...d.stockIsActive,
                                        value: val
                                    }
                                }))}
                                data={[
                                    { label: 'Aktif', value: '1' },
                                    { label: 'Non Aktif', value: '0' },
                                ]}
                            />
                        </InputWrapperCustom>
                    </Box>

                    <Group position="apart" noWrap spacing='xs' my='xl'>
                        <Button
                            fullWidth
                            variant="default"
                            color='gray'
                            onClick={resetData}
                        >
                            Reset
                        </Button>

                        <Button
                            fullWidth
                            // variant="default"
                            // color='teal'
                            loading={data.submitLoading}
                            onClick={editData}
                        >
                            Submit
                        </Button>
                    </Group>

                </Box>
            </Drawer>

            <StockCategorySubSelectorDrawer
                stateShow={data.stockCategorySub.value.show}

                fnShow={() => setData(d => ({
                    ...d,
                    stockCategorySub: {
                        ...d.stockCategorySub,
                        value: {
                            ...d.stockCategorySub.value,
                            show: false
                        }
                    }
                }))}

                stateData={{
                    categoryId: utils.flagAll,
                    categoryIsActive: '1'
                }}

                fnOnSelect={(object) => setData(d => ({
                    ...d,
                    stockCategorySub: {
                        ...d.stockCategorySub,
                        value: {
                            ...d.stockCategorySub.value,
                            id: object.stockCategorySubId,
                            name: object.stockCategorySubName,
                            isActive: object.stockCategorySubIsActive,
                            categoryId: object.stockCategoryId,
                            categoryName: object.stockCategoryName,
                            show: false
                        }
                    }
                }))}
            />

            <SupplierSelectorDrawer
                stateShow={data.stockSupplier.value.show}

                fnShow={() => setData(d => ({
                    ...d,
                    stockSupplier: {
                        ...d.stockSupplier,
                        value: {
                            ...d.stockSupplier.value,
                            show: false
                        }
                    }
                }))}

                stateData={{
                    supplierIsActive: '1'
                }}

                fnOnSelect={(object) => {
                    setData(d => ({
                        ...d,
                        stockSupplier: {
                            ...d.stockSupplier,
                            value: {
                                ...d.stockSupplier.value,
                                id: object.supplierId,
                                name: object.supplierName,
                                address: object.supplierAddress,
                                contact: object.supplierContact,
                                isActive: object.supplierIsActive,
                                show: false
                            }
                        }
                    }))
                }}
            />

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />

            <ScannerBarQRCodeModul
                stateData={{
                    show: data.scannerModal,
                }}

                fnSuccess={(val) => {
                    setData(d => ({
                        ...d,
                        stockItem: {
                            ...d.stockItem,
                            value: {
                                ...d.stockItem.value,
                                serialNumber: regexNoSpace(val),
                                jumlah: val ? '1' : d.stockItem.value.jumlah
                            }
                        },
                        scannerModal: false
                    }))
                }}

                fnClose={(val) => setData(d => ({ ...d, scannerModal: val }))}

                fnFailed={(val) => { }}
            />
        </React.Fragment>
    )
}

export default StockItemEdit;